<template>
  <CommonLoading
		v-if="loading"
	/>
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-4xl sm:p-6">
              <!-- Header -->
              <div class="flex justify-between items-start border-b border-gray-200 pb-4">
                <div>
                  <h2 class="text-xl font-bold leading-9 tracking-tight text-gray-900">Novo Segurado</h2>
                  <p class="text-sm text-gray-500">Preencha as informações para cadastrar um novo segurado no sistema.</p>
                </div>
                <button @click="closeDialog" type="button" class="text-gray-400 hover:text-gray-600 -mt-2">
                  <span class="sr-only">Fechar</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Form -->
              <div class="mt-6 space-y-6">
                <!-- Campos principais -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <!-- Nome -->
                  <div class="flex flex-col">
                    <label for="nome" class="text-sm font-medium text-gray-900">Nome <span class="text-red-500">*</span></label>
                    <input
                      v-model="formData.nome"
                      id="nome"
                      type="text"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Digite o Nome completo"
                      required
                    />
                  </div>

                  <!-- CPF -->
                  <div class="flex flex-col">
                    <label for="cpf" class="text-sm font-medium text-gray-900">CPF <span class="text-red-500">*</span></label>
                    <input
                      v-model="formData.cpf"
                      id="cpf"
                      type="text"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="000.000.000-00"
                      @input="formatCpf"
                      required
                    />
                  </div>

                  <!-- RG -->
                  <div class="flex flex-col">
                    <label for="rg" class="text-sm font-medium text-gray-900">RG</label>
                    <input
                      v-model="formData.rg"
                      id="rg"
                      type="text"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Digite o RG"
                    />
                  </div>

                  <!-- Data de Nascimento -->
                  <div class="flex flex-col">
                    <label for="data_nascimento" class="text-sm font-medium text-gray-900">Data de Nascimento <span class="text-red-500">*</span></label>
                    <input
                      v-model="formData.data_nascimento"
                      id="data_nascimento"
                      type="date"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    />
                  </div>

                  <!-- Idade (Calculada dinamicamente) -->
                  <div class="flex flex-col">
                    <label for="idade" class="text-sm font-medium text-gray-900">Idade (Calculada)</label>
                    <input
                      v-model="formData.idade"
                      id="idade"
                      type="number"
                      class="mt-1 block w-full rounded-md border-gray-200 bg-gray-50 shadow-sm sm:text-sm text-gray-600"
                      placeholder="Calculada automaticamente"
                      disabled
                    />
                  </div>

                  <!-- E-mail -->
                  <div class="flex flex-col">
                    <label for="email" class="text-sm font-medium text-gray-900">E-mail <span class="text-red-500">*</span></label>
                    <input
                      v-model="formData.email"
                      id="email"
                      type="email"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="exemplo@email.com"
                      required
                    />
                  </div>

                  <!-- Telefone -->
                  <div class="flex flex-col">
                    <label for="telefone" class="text-sm font-medium text-gray-900">Telefone</label>
                    <input
                      v-model="formData.telefone"
                      id="telefone"
                      type="text"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="(00) 00000-0000"
                      @input="formatTelefone"
                    />
                  </div>

                  <!-- Carreira -->
                  <div class="flex flex-col">
                    <label for="carreira" class="text-sm font-medium text-gray-900">Carreira</label>
                    <input
                      v-model="formData.carreira"
                      id="carreira"
                      type="text"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Digite a Carreira"
                    />
                  </div>

                  <!-- Status -->
                  <div class="flex flex-col">
                    <label for="status" class="text-sm font-medium text-gray-900">Status <span class="text-red-500">*</span></label>
                    <select
                      v-model="formData.status"
                      id="status"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                    </select>
                  </div>

                  <!-- Tipo Sócio -->
                  <div class="flex flex-col">
                    <label for="tipo_socio" class="text-sm font-medium text-gray-900">Tipo Sócio</label>
                    <input
                      v-model="formData.tipo_socio"
                      id="tipo_socio"
                      type="text"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Ex: Segurado"
                    />
                  </div>
                </div>

                <!-- Divider Endereços -->
                <div class="relative py-2">
                  <div class="absolute inset-0 flex items-center" aria-hidden="true">
                    <div class="w-full border-t border-gray-200" />
                  </div>
                  <div class="relative flex items-center justify-between">
                    <span class="bg-white pr-3 text-base font-semibold text-gray-900">Endereços</span>
                    <button
                      @click="addItem('enderecos')"
                      type="button"
                      class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-300 hover:bg-gray-50 transition-colors"
                    >
                      <PlusIcon class="-ml-1 -mr-0.5 size-4 text-indigo-600" aria-hidden="true" />
                      <span>Adicionar Endereço</span>
                    </button>
                  </div>
                </div>

                <!-- Sub-Form Endereços dinâmicos -->
                <div v-if="formData.enderecos.length === 0" class="text-center py-4 text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  Nenhum endereço adicionado. Clique no botão acima para adicionar se desejar.
                </div>
                <div v-else class="space-y-6">
                  <div
                    v-for="(endereco, index) in formData.enderecos"
                    :key="index"
                    class="p-4 rounded-lg border border-gray-200 bg-gray-50 space-y-4 relative"
                  >
                    <div class="flex justify-between items-center">
                      <span class="text-sm font-semibold text-gray-700">Endereço #{{ index + 1 }}</span>
                      <button
                        @click="removeItem('enderecos', index)"
                        type="button"
                        class="text-xs text-red-600 hover:text-red-800 font-semibold"
                      >
                        Remover Endereço
                      </button>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <!-- CEP -->
                      <div class="flex flex-col">
                        <label :for="'cep-' + index" class="text-xs font-medium text-gray-700">CEP <span class="text-red-500">*</span></label>
                        <input
                          v-model="endereco.cep"
                          :id="'cep-' + index"
                          type="text"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs"
                          placeholder="00000-000"
                          @input="formatCep(index)"
                          required
                        />
                      </div>

                      <!-- Logradouro -->
                      <div class="flex flex-col col-span-1 sm:col-span-2">
                        <label :for="'logradouro-' + index" class="text-xs font-medium text-gray-700">Logradouro <span class="text-red-500">*</span></label>
                        <input
                          v-model="endereco.logradouro"
                          :id="'logradouro-' + index"
                          type="text"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs"
                          placeholder="Rua, Avenida, etc."
                          required
                        />
                      </div>

                      <!-- Número -->
                      <div class="flex flex-col">
                        <label :for="'numero-' + index" class="text-xs font-medium text-gray-700">Número <span class="text-red-500">*</span></label>
                        <input
                          v-model="endereco.numero"
                          :id="'numero-' + index"
                          type="text"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs"
                          placeholder="Nº"
                          required
                        />
                      </div>

                      <!-- Complemento -->
                      <div class="flex flex-col">
                        <label :for="'complemento-' + index" class="text-xs font-medium text-gray-700">Complemento</label>
                        <input
                          v-model="endereco.complemento"
                          :id="'complemento-' + index"
                          type="text"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs"
                          placeholder="Apto, Sala, Bloco..."
                        />
                      </div>

                      <!-- Bairro -->
                      <div class="flex flex-col">
                        <label :for="'bairro-' + index" class="text-xs font-medium text-gray-700">Bairro <span class="text-red-500">*</span></label>
                        <input
                          v-model="endereco.bairro"
                          :id="'bairro-' + index"
                          type="text"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs"
                          placeholder="Bairro"
                          required
                        />
                      </div>

                      <!-- Cidade -->
                      <div class="flex flex-col">
                        <label :for="'cidade-' + index" class="text-xs font-medium text-gray-700">Cidade <span class="text-red-500">*</span></label>
                        <input
                          v-model="endereco.cidade"
                          :id="'cidade-' + index"
                          type="text"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs"
                          placeholder="Cidade"
                          required
                        />
                      </div>

                      <!-- Estado -->
                      <div class="flex flex-col">
                        <label :for="'estado-' + index" class="text-xs font-medium text-gray-700">Estado (UF) <span class="text-red-500">*</span></label>
                        <input
                          v-model="endereco.estado"
                          :id="'estado-' + index"
                          type="text"
                          maxlength="2"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs"
                          placeholder="EX: PR"
                          @input="formatUf(index)"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Footer / Actions -->
                <div class="flex justify-end gap-3 border-t border-gray-200 pt-4 mt-6">
                  <button
                    @click="closeDialog"
                    type="button"
                    class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Cancelar
                  </button>
                  <button
                    @click="saveData"
                    type="button"
                    class="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import Swal from "sweetalert2";
import CommonLoading from '@/components/generalcomponents/CommonLoading.vue';
import { PlusIcon } from '@heroicons/vue/20/solid';

export default {
  name: 'SeguradoForm',
  emits: ['update-open', 'save'],
  props: {
    open: {
      type: Boolean,
      required: true,
    }
  },
  data() {
    return {
      loading: false,
      formData: {
        nome: '',
        cpf: '',
        rg: '',
        data_nascimento: '',
        idade: null,
        email: '',
        telefone: '',
        carreira: '',
        status: 'ativo',
        tipo_socio: 'segurado',
        enderecos: []
      }
    };
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
    'formData.data_nascimento'(newVal) {
      if (newVal) {
        this.formData.idade = this.calculateAge(newVal);
      } else {
        this.formData.idade = null;
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('update-open', false);
    },
    resetForm() {
      this.formData = {
        nome: '',
        cpf: '',
        rg: '',
        data_nascimento: '',
        idade: null,
        email: '',
        telefone: '',
        carreira: '',
        status: 'ativo',
        tipo_socio: 'segurado',
        enderecos: []
      };
    },
    calculateAge(birthDateString) {
      if (!birthDateString) return null;
      const today = new Date();
      const birthDate = new Date(birthDateString);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age >= 0 ? age : 0;
    },
    formatCpf() {
      let value = this.formData.cpf.replace(/\D/g, '');
      if (value.length > 11) value = value.slice(0, 11);
      
      if (value.length > 9) {
        this.formData.cpf = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      } else if (value.length > 6) {
        this.formData.cpf = value.replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3');
      } else if (value.length > 3) {
        this.formData.cpf = value.replace(/(\d{3})(\d{3})/, '$1.$2');
      } else {
        this.formData.cpf = value;
      }
    },
    formatTelefone() {
      let value = this.formData.telefone.replace(/\D/g, '');
      if (value.length > 11) value = value.slice(0, 11);

      if (value.length === 11) {
        this.formData.telefone = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      } else if (value.length > 6) {
        this.formData.telefone = value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      } else if (value.length > 2) {
        this.formData.telefone = value.replace(/(\d{2})(\d{0,5})/, '($1) $2');
      } else {
        this.formData.telefone = value;
      }
    },
    formatCep(index) {
      let value = this.formData.enderecos[index].cep.replace(/\D/g, '');
      if (value.length > 8) value = value.slice(0, 8);

      if (value.length > 5) {
        this.formData.enderecos[index].cep = value.replace(/(\d{5})(\d{3})/, '$1-$2');
      } else {
        this.formData.enderecos[index].cep = value;
      }
    },
    formatUf(index) {
      const value = this.formData.enderecos[index].estado;
      this.formData.enderecos[index].estado = value.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
    },
    addItem(type) {
      if (type === 'enderecos') {
        // Valida se o último endereço está preenchido antes de adicionar outro
        if (this.formData.enderecos.length > 0) {
          const lastIndex = this.formData.enderecos.length - 1;
          const lastEnd = this.formData.enderecos[lastIndex];
          if (!lastEnd.cep || !lastEnd.logradouro || !lastEnd.numero || !lastEnd.bairro || !lastEnd.cidade || !lastEnd.estado) {
            this.mostrarAlertaErro('Preencha os dados do endereço atual antes de adicionar outro.');
            return;
          }
        }
        
        this.formData.enderecos.push({
          cep: '',
          logradouro: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          estado: ''
        });
      }
    },
    removeItem(type, index) {
      if (type === 'enderecos') {
        this.formData.enderecos.splice(index, 1);
      }
    },
    saveData() {
      // Validações básicas obrigatórias no front e no back
      if (!this.formData.nome || !this.formData.nome.trim()) {
        this.mostrarAlertaErro('O campo Nome é obrigatório.');
        return;
      }
      if (!this.formData.cpf || this.formData.cpf.replace(/\D/g, '').length !== 11) {
        this.mostrarAlertaErro('Por favor, informe um CPF válido (11 dígitos).');
        return;
      }
      if (!this.formData.data_nascimento) {
        this.mostrarAlertaErro('A Data de Nascimento é obrigatória.');
        return;
      }
      if (!this.formData.email || !this.formData.email.trim()) {
        this.mostrarAlertaErro('O campo E-mail é obrigatório.');
        return;
      }
      if (this.formData.idade === null || this.formData.idade < 1) {
        this.mostrarAlertaErro('O segurado deve ter idade maior ou igual a 1 ano.');
        return;
      }

      // Validações dos endereços
      const extraFields = [];
      for (let i = 0; i < this.formData.enderecos.length; i++) {
        const end = this.formData.enderecos[i];
        if (!end.cep || !end.logradouro || !end.numero || !end.bairro || !end.cidade || !end.estado) {
          this.mostrarAlertaErro(`Preencha todos os campos obrigatórios do Endereço #${i + 1}.`);
          return;
        }
        extraFields.push({
          logradouro: end.logradouro.trim(),
          numero: end.numero.trim(),
          complemento: end.complemento ? end.complemento.trim() : null,
          bairro: end.bairro.trim(),
          cidade: end.cidade.trim(),
          estado: end.estado.trim().toUpperCase(),
          cep: end.cep.replace(/\D/g, '') // normaliza CEP
        });
      }

      const payload = {
        nome: this.formData.nome.trim(),
        cpf: this.formData.cpf.replace(/\D/g, ''), // normaliza CPF
        rg: this.formData.rg ? this.formData.rg.replace(/\D/g, '') : null,  // normaliza RG
        data_nascimento: this.formData.data_nascimento,
        idade: parseInt(this.formData.idade),
        email: this.formData.email.trim().toLowerCase(),
        telefone: this.formData.telefone ? this.formData.telefone.replace(/\D/g, '') : '', // normaliza Telefone
        carreira: this.formData.carreira ? this.formData.carreira.trim() : null,
        status: this.formData.status,
        tipo_socio: this.formData.tipo_socio ? this.formData.tipo_socio.trim() : 'segurado',
        extraFields: extraFields
      };

      this.$emit('save', payload);
      this.closeDialog();
    },
    mostrarAlertaErro(message) {
      Swal.fire({
        icon: 'error',
        title: 'Atenção!',
        text: message,
        confirmButtonColor: '#4f46e5'
      });
    }
  },
  components: {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    CommonLoading,
    PlusIcon
  }
};
</script>

<style scoped>
</style>
